/**
 * InvoiceForm Component
 * Text input for natural language invoice generation
 */

import { useState, useEffect } from 'react';
import { invoiceExamples } from '@/constants/invoiceExamples';
import { useTranslation } from 'react-i18next';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { LogoUploader } from './LogoUploader';
import { SignStampUploader } from './SignStampUploader';
import { InvoiceTurnstile } from '@/components/turnstile/InvoiceTurnstile';
import { getDefaultLogo, setDefaultLogo, clearDefaultLogo, isDefaultLogo } from '@/utils/logoStorage';
import {
  getDefaultSignStamp,
  setDefaultSignStamp,
  clearDefaultSignStamp,
  isDefaultSignStamp,
} from '@/utils/signStampStorage';
import { logger } from '@/utils/logger';
import {
  PaperPreset,
  createPaperSize,
} from '@/types/paper';
import { sanitizeInvoiceText } from '@/lib/utils/sanitize';

interface InvoiceFormProps {
  onGenerate: (text: string) => void;
  isGenerating: boolean;
}

export function InvoiceForm({
  onGenerate,
  isGenerating,
}: InvoiceFormProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [documentType, setDocumentType] = useState<'invoice' | 'receipt'>('invoice');
  const error = useInvoiceStore((state) => state.error);
  const setError = useInvoiceStore((state) => state.setError);
  const { currentInvoice, paperSize, setPaperSize, rateLimitStatus } = useInvoiceStore();

  // Turnstile state for bot protection
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileRequired, setTurnstileRequired] = useState(false);

  // Check if Turnstile verification is needed
  useEffect(() => {
    const requestCount = parseInt(localStorage.getItem('invoice-request-count') || '0');
    const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

    // Only require Turnstile if site key is configured AND after 3rd request
    if (turnstileSiteKey && requestCount >= 3) {
      setTurnstileRequired(true);
    }
  }, []);

  // Separate state for InvoiceForm's default logo
  const [formLogo, setFormLogo] = useState<string | undefined>(() => getDefaultLogo() || undefined);

  // Separate state for InvoiceForm's default sign/stamp
  const [formSignStamp, setFormSignStamp] = useState<string | undefined>(() => getDefaultSignStamp() || undefined);

  // Update formLogo when default logo changes in storage
  useEffect(() => {
    const defaultLogo = getDefaultLogo();
    logger.info('[InvoiceForm] Default logo from storage:', defaultLogo ? 'Found' : 'Not found');

    // Only update formLogo if:
    // 1. There's a default logo in storage AND
    // 2. Either there's no currentInvoice, OR the currentInvoice is using the default logo
    if (defaultLogo) {
      if (!currentInvoice?.fromLogo) {
        logger.info('[InvoiceForm] Setting formLogo from storage (no current invoice logo)');
        setFormLogo(defaultLogo);
      } else if (isDefaultLogo(currentInvoice.fromLogo)) {
        logger.info('[InvoiceForm] Current invoice uses default logo, syncing');
        setFormLogo(currentInvoice.fromLogo);
      } else {
        logger.info('[InvoiceForm] Current invoice has custom logo, keeping formLogo from storage');
        setFormLogo(defaultLogo);
      }
    } else {
      logger.info('[InvoiceForm] No default logo in storage');
      setFormLogo(undefined);
    }
  }, [currentInvoice?.fromLogo]);

  // Update formSignStamp when default sign/stamp changes in storage
  useEffect(() => {
    const defaultSignStamp = getDefaultSignStamp();
    logger.info('[InvoiceForm] Default sign/stamp from storage:', defaultSignStamp ? 'Found' : 'Not found');

    // Only update formSignStamp if:
    // 1. There's a default sign/stamp in storage AND
    // 2. Either there's no currentInvoice, OR the currentInvoice is using the default sign/stamp
    if (defaultSignStamp) {
      if (!currentInvoice?.fromSignStamp) {
        logger.info('[InvoiceForm] Setting formSignStamp from storage (no current invoice sign/stamp)');
        setFormSignStamp(defaultSignStamp);
      } else if (isDefaultSignStamp(currentInvoice.fromSignStamp)) {
        logger.info('[InvoiceForm] Current invoice uses default sign/stamp, syncing');
        setFormSignStamp(currentInvoice.fromSignStamp);
      } else {
        logger.info('[InvoiceForm] Current invoice has custom sign/stamp, keeping formSignStamp from storage');
        setFormSignStamp(defaultSignStamp);
      }
    } else {
      logger.info('[InvoiceForm] No default sign/stamp in storage');
      setFormSignStamp(undefined);
    }
  }, [currentInvoice?.fromSignStamp]);

  const handleLogoChange = (logoData: string | null) => {
    logger.info('[InvoiceForm] Logo changed:', logoData ? 'New logo uploaded' : 'Logo removed');

    if (logoData) {
      // Save as new default logo
      setDefaultLogo(logoData);
      setFormLogo(logoData);
      logger.info('[InvoiceForm] Saved new default logo to storage');
    } else {
      // Clear default logo
      clearDefaultLogo();
      setFormLogo(undefined);
      logger.info('[InvoiceForm] Cleared default logo from storage');
    }
  };

  const handleSignStampChange = (signStampData: string | null) => {
    logger.info('[InvoiceForm] Sign/stamp changed:', signStampData ? 'New sign/stamp uploaded' : 'Sign/stamp removed');

    if (signStampData) {
      // Save as new default sign/stamp
      setDefaultSignStamp(signStampData);
      setFormSignStamp(signStampData);
      logger.info('[InvoiceForm] Saved new default sign/stamp to storage');
    } else {
      // Clear default sign/stamp
      clearDefaultSignStamp();
      setFormSignStamp(undefined);
      logger.info('[InvoiceForm] Cleared default sign/stamp from storage');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Require Turnstile verification if it's enabled
    if (turnstileRequired && !turnstileToken) {
      setError(t('invoice.form.turnstileRequired', 'Please complete the verification first'));
      return;
    }

    if (text.trim().length >= 10 && !isGenerating) {
      // Include document type in the text for AI processing
      const textWithDocType = `[Document Type: ${documentType.toUpperCase()}]\n\n${text.trim()}`;

      // Sanitize input to prevent XSS attacks
      const sanitizedText = sanitizeInvoiceText(textWithDocType);

      // Increment request count
      const count = parseInt(localStorage.getItem('invoice-request-count') || '0');
      localStorage.setItem('invoice-request-count', String(count + 1));

      // Trigger Turnstile requirement for next request if site key is configured AND this is the 2nd or 3rd request
      const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
      if (turnstileSiteKey && count + 1 >= 3) {
        setTurnstileRequired(true);
      }

      onGenerate(sanitizedText);
    }
  };

  const canGenerate = text.trim().length >= 10 && !isGenerating && !rateLimitStatus.isLimited;

  // Format time until rate limit reset
  const getTimeUntilReset = (): string => {
    if (!rateLimitStatus.resetAt) return '';

    const now = new Date();
    const resetTime = new Date(rateLimitStatus.resetAt);
    const diffMs = resetTime.getTime() - now.getTime();

    if (diffMs <= 0) return '';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <>


      <div className="card dark:bg-gray-800 dark:border-gray-700">
        <h2 className="card-header dark:text-gray-100">{t('invoice.form.title')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('invoice.form.description')}</p>

        {/* Error display */}
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Rate limit warning */}
        {rateLimitStatus.isLimited && (
          <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              ⚠️ {t('invoice.form.rateLimitReached', 'Daily limit reached')}
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
              {t('invoice.form.remainingToday', 'Remaining today')}: {rateLimitStatus.remaining}
            </p>
            {rateLimitStatus.resetAt && (
              <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                {t('invoice.form.resetsIn', 'Resets in')}: {getTimeUntilReset()}
              </p>
            )}
          </div>
        )}

        {/* Rate limit info (not limited) */}
        {!rateLimitStatus.isLimited && rateLimitStatus.remaining < 5 && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              ℹ️ {t('invoice.form.remainingToday', 'Remaining today')}: {rateLimitStatus.remaining}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="invoice-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('invoice.form.invoiceText')}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setText(invoiceExamples.example1)}
                  className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                  disabled={isGenerating}
                >
                  Example 1
                </button>
                <button
                  type="button"
                  onClick={() => setText(invoiceExamples.example2)}
                  className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                  disabled={isGenerating}
                >
                  Example 2
                </button>
                <button
                  type="button"
                  onClick={() => setText(invoiceExamples.example3)}
                  className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                  disabled={isGenerating}
                >
                  Example 3
                </button>
              </div>
            </div>
            <textarea
              id="invoice-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('invoice.form.invoiceTextPlaceholder')}
              className="input-field min-h-[150px] resize-y dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
              disabled={isGenerating}
            />
            <div className="mt-2 flex justify-between items-center">
              <span className={`text-sm ${text.length < 10 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                {text.length} {t('invoice.editor.characters')}
              </span>
            </div>
          </div>

          {/* Logo and Sign/Stamp Upload Section */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('invoice.editor.optionalImages')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Logo */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('invoice.editor.companyLogo')}
              </p>
              <LogoUploader
                currentLogo={formLogo}
                onLogoChange={handleLogoChange}
              />
            </div>

            {/* Sign/Stamp */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('invoice.editor.signStamp')}
              </p>
              <SignStampUploader
                currentSignStamp={formSignStamp}
                onSignStampChange={handleSignStampChange}
              />
            </div>
          </div>

          {/* Document Type and Paper Size Section */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Document Type Selection */}
              <div>
                <label htmlFor="document-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document Type
                </label>
                <select
                  id="document-type"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value as 'invoice' | 'receipt')}
                  className="px-3 py-2 w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isGenerating}
                >
                  <option value="invoice">Invoice</option>
                  <option value="receipt">Receipt</option>
                </select>
              </div>

              {/* Paper Size Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('invoice.editor.paperSize', 'Paper Size')}
                </label>
                <div className="flex gap-2">
                  <select
                    value={paperSize.preset}
                    onChange={(e) => setPaperSize(createPaperSize(e.target.value as PaperPreset, paperSize.orientation))}
                    className="px-3 py-2 flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {(['A4', 'A5', 'Letter', 'Legal'] as PaperPreset[]).map((preset) => (
                      <option key={preset} value={preset}>
                        {preset}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => setPaperSize(createPaperSize(
                      paperSize.preset,
                      paperSize.orientation === 'portrait' ? 'landscape' : 'portrait'
                    ))}
                    className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                    aria-label={`Current orientation: ${paperSize.orientation}, click to change`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {paperSize.orientation === 'portrait' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 2v4m0 0l-4 4m0 0l4-4m0 0v4m0 0H4m16 0h-8M4 18h16" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      )}
                    </svg>
                    <span className="capitalize">{paperSize.orientation}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Turnstile Verification (shown after 3rd request) */}
          {turnstileRequired && (
            <InvoiceTurnstile
              onVerify={(token: string) => setTurnstileToken(token)}
              onError={() => {
                setTurnstileToken(null);
                setError(t('invoice.form.turnstileError', 'Verification failed. Please try again.'));
              }}
            />
          )}

          <button
            type="submit"
            disabled={!canGenerate}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {rateLimitStatus.isLimited ? (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t('invoice.form.rateLimitedButton', 'Rate Limited')}
              </>
            ) : isGenerating ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('invoice.form.generatingButton')}
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                {t('invoice.form.generateButton')}
              </>
            )}
          </button>
        </form>

      </div>
      {/* Inline Ad at the bottom TODO: enable later*/}
      {/* <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
        <InlineAd />
      </div> */}
    </>
  );
}
